import { Theme, createStyles, withStyles, WithStyles, AppBar, Button, FormControl, FormControlLabel, FormLabel, Grid, List, ListItem, ListItemText, Paper, Radio, RadioGroup, Switch, TextField, Toolbar, Typography, IconButton, Divider } from '@material-ui/core';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import FileInput, { InputIdentifier } from './FileInput';
import FileUploader from './FileUploader';
import { Operations, NavigatorData } from './NavigatorData';
import { OperationContext } from './OperationContext';
import ProgressIndicator from './ProgressIndicator';

//importing all material-ui icons here 
import SendIcon from '@material-ui/icons/Send';
import DescriptionIcon from '@material-ui/icons/Description';
import Link from '@material-ui/core/Link';
import GetAppIcon from '@material-ui/icons/GetApp';
import FormDataType, { ActionPayload, ArrayAlignmentName, WordByWordName, ThreeWayMergeModeName, MergePriorityName, PatchDirectionName, GraftResolutionModeName, ResultFilename, ResultFileState as ResultFileStateEnum, JobDescriptor } from './ConfigFormData';
import { ArrayAlignment, GraftResolutionMode, MergePriority, PatchDirection, ThreeWayMergeMode } from '../api/commonConfig';
import { ProgressDataType } from './Paperbase';
import ResultFileIcon from './ResultFileIcon';
import DialogButton from './DialogButton';
import InfoIcon from '@material-ui/icons/Info';
import { Themes } from '../styling/Themes';
import { DeltaXMLColors, DeltaJSONColors } from '../styling/DeltaColors';
import { CustomerSubscriptionStatus, CustomerSubscription } from './SubscriptionData';

const styles = (theme: Theme) =>
    createStyles({
        paper: {
            maxWidth: 936,
            margin: 'auto',
            overflow: 'hidden',
        },
        searchBar: {
            borderBottom: '1px solid '+ DeltaJSONColors.MERCURY,
            backgroundColor: DeltaXMLColors.CONCRETE,
        },
        contentWrapper: {
            margin: '5px 5px 0px 5px',
        },
        labelSpace: {
            marginLeft: '10px',
            cursor: 'default',
            color: DeltaXMLColors.OXFORD_BLUE,
        },
        label: {
            marginLeft: '10px',
            cursor: 'default'
        },
        infoText: {
            textAlign: 'left'
        },
        optionLabel: {
            marginLeft: '10px',
        },
        fileLabel: {
            marginStart: '1em',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '300px',
            overflow: 'hidden'
        },
        fileButton: {
            display: 'inline-flex',
            marginEnd: '4px',
            backgroundColor: 'transparent',
            border: '1px solid',
            color: DeltaXMLColors.OXFORD_BLUE,
            padding: '5px 10px',
            marginStart: '0px',
            '&:hover': {
                // Set hover color
                backgroundColor: DeltaXMLColors.OXFORD_BLUE,
                color: DeltaJSONColors.WHITE,
                border: '1px solid ' + DeltaXMLColors.OXFORD_BLUE,
                boxShadow: 'none'
            },
            width: '131px',
            height: '32px'
        },
        resultFileIcon: {
            color: DeltaXMLColors.FOUNTAIN_BLUE
        },
        comparePaper: {
            padding: theme.spacing(1, 2),
            textAlign: 'left',
            marginBottom: '1em',
        },
        /* samples list styles */
        root: {
            width: '100%',
            maxWidth: '36ch',
        },
        inline: {
            display: 'inline',
        },
        errorGridItem: {
            paddingTop: '9px'
        },
        listItem: {
            padding: theme.spacing(0.5),
            marginBottom: '0.5px',
        },
        link: {
            textDecoration: 'underline',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color: DeltaXMLColors.FOUNTAIN_BLUE
        },
        jobDescriptor: {
            marginLeft: '10px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '310px',
            overflow: 'hidden',
            padding: '0px 5px 0px 5px',
        },
        formLabelButton: {
            marginEnd: '20px',
            backgroundColor: 'transparent',
            padding: '5px 0px 0px 0px',
            marginStart: '15px',
            cursor: "default",
            color: DeltaXMLColors.OXFORD_BLUE,
        },
        divider: {
            backgroundColor: DeltaJSONColors.MERCURY,
            margin: '4px 0px 4px 0px'
        },
        divider1: {
            backgroundColor: DeltaJSONColors.MERCURY,
            margin: '8px 0px 15px 0px'
        },
        icon: {
            color: DeltaXMLColors.OXFORD_BLUE
        },
        info: {
            color: DeltaXMLColors.FOUNTAIN_BLUE
        }
    });

export interface ConfigContentProps extends WithStyles<typeof styles> {
    operationType: Operations;
    onFormChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFormDrop: (event: React.DragEvent<HTMLInputElement>, InputFilename: string) => void;
    formValues: FormDataType;
    updateFormValues: React.Dispatch<ActionPayload>;
    runOperation: () => void;
    downloadResult: () => void;
    allProgressData: ProgressDataType,
    onClearInputs: () => void;
    customerSubscriptionData: CustomerSubscription
}

function ConfigContent(props: ConfigContentProps) {
    const opContext = React.useContext(OperationContext);
    const { classes, formValues, operationType, allProgressData, onClearInputs, customerSubscriptionData } = props;
    const configFormValues = formValues[operationType];
    const { ErrorData, ResultFileState } = configFormValues;
    opContext.handleConfigOperationChange(operationType);
    const handleChange = props.onFormChange;
    const handledrop = props.onFormDrop;
    const runOperation = props.runOperation;
    const downloadResult = props.downloadResult;

    const isGraftOrPatch = opContext.operationType === Operations.graft || opContext.operationType === Operations.patch;

    const enableRunButton = configFormValues.isDirty && configFormValues.isValid && !(configFormValues.ResultFileState === ResultFileStateEnum.inProgress);

    const starterSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.FREE;
    const professionalSubscription: boolean = customerSubscriptionData.subscriptionStatus === CustomerSubscriptionStatus.PAID;

    return (
        <OperationContext.Consumer>
            {context => context && (
                <Paper className={classes.paper}>
                    <AppBar
                        id={operationType.toString()}
                        className={classes.searchBar}
                        position="static"
                        elevation={0}>
                        <Toolbar>
                            <Grid container spacing={1} direction="row">
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        endIcon={<SendIcon />}
                                        onClick={runOperation}
                                        disabled={!enableRunButton}>
                                        Run
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <NavLink to={NavigatorData.filesBaseUrl} style={{ textDecoration: 'none' }}>
                                        <Button variant="contained" color="primary" endIcon={<DescriptionIcon />}>
                                            View Files
                                        </Button>
                                    </NavLink>
                                </Grid>
                                <Grid item>
                                    <DialogButton onClearInputs={onClearInputs} />
                                </Grid>
                                <Grid item xs>
                                    <ProgressIndicator percentage={allProgressData[opContext.operationType].percentage} operation={opContext.operationType} resultFileState={ResultFileState} />
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <div className={classes.contentWrapper} >
                        <Grid container spacing={3}>
                            <Grid item xs={8}>
                                <Grid item xs={12}>
                                    <Paper className={classes.comparePaper}>
                                        <Grid item xs={12} style={{ alignItems: 'left' }}>
                                            <Grid container>
                                                <Grid item xs={11}>
                                                    <FormControlLabel
                                                        className={classes.formLabelButton}
                                                        control={<></>}
                                                        label='Job Description'
                                                        labelPlacement="start"
                                                    />
                                                    <TextField className={classes.jobDescriptor} onChange={handleChange} spellCheck={false} name={JobDescriptor} value={configFormValues.JobDescriptor} />
                                                </Grid>
                                                <Grid item xs={1} style={{ paddingTop: '9px' }}>
                                                    <Themes.HtmlTooltip
                                                        arrow
                                                        interactive
                                                        title={
                                                            <Typography variant="body2" color="inherit">
                                                                Description for this job - displayed in the Job History. Also affects the result-filename.
                                                            </Typography>
                                                        }>
                                                        <InfoIcon className={classes.info}/>
                                                    </Themes.HtmlTooltip>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Divider className={classes.divider1} />
                                        {configFormValues.AncestorFilename && configFormValues.AncestorFile !== undefined && (
                                            <Grid item xs={12}>
                                                <FileUploader onFormDrop={handledrop} formValues={configFormValues} inputIdentifier={InputIdentifier.Ancestor}>
                                                    <FileInput onFormChange={handleChange} inputFilename={configFormValues.AncestorFilename}
                                                        inputFile={configFormValues.AncestorFile} inputIdentifier={InputIdentifier.Ancestor} errorMessage={ErrorData.ancestorFile} />
                                                </FileUploader>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <FileUploader onFormDrop={handledrop} formValues={configFormValues} inputIdentifier={InputIdentifier.A}>
                                                <FileInput onFormChange={handleChange} inputFilename={configFormValues.InputFilenameA}
                                                    inputFile={configFormValues.InputFileA} inputIdentifier={opContext.operationType !== Operations.graft ? InputIdentifier.A : InputIdentifier.Changeset} errorMessage={ErrorData.inputFileA} />
                                            </FileUploader>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FileUploader onFormDrop={handledrop} formValues={configFormValues} inputIdentifier={InputIdentifier.B}>
                                                <FileInput onFormChange={handleChange} inputFilename={configFormValues.InputFilenameB}
                                                    inputFile={configFormValues.InputFileB} inputIdentifier={opContext.operationType !== Operations.graft ? InputIdentifier.B : InputIdentifier.Target} errorMessage={ErrorData.inputFileB} />
                                            </FileUploader>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={11}>
                                                    <Button
                                                        className={classes.fileButton}
                                                        endIcon={<GetAppIcon className={classes.resultFileIcon} />}
                                                        onClick={downloadResult}
                                                        disabled={!(ResultFileState === ResultFileStateEnum.completedOk || ResultFileState === ResultFileStateEnum.completedOkButStale)}>
                                                        Result File
                                                    </Button>
                                                    <TextField onChange={handleChange} spellCheck={false} className={classes.fileLabel} id="fileResult" value={configFormValues.ResultFilename} name={ResultFilename}
                                                        error={ErrorData.resultFile !== null} helperText={ErrorData.resultFile !== null ? ErrorData.resultFile : 'Auto-updated unique filename (editable)'} />
                                                </Grid>
                                                <Grid item xs={1} className={classes.errorGridItem}>
                                                    <ResultFileIcon state={ResultFileState} />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Divider className={classes.divider} />
                                        {!isGraftOrPatch && (
                                            <Grid item xs={12}>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        value="start"
                                                        control={<Switch name={WordByWordName} color="primary" checked={configFormValues.WordByWordOption} onChange={handleChange} />}
                                                        label="Word by Word"
                                                        labelPlacement="start"
                                                        className={classes.labelSpace}
                                                        disabled={!professionalSubscription}
                                                    />
                                                    <Themes.HtmlTooltip
                                                        arrow
                                                        interactive
                                                        title={
                                                            <Typography variant="body2" color="inherit">
                                                                This option provides more granularity for changes to values. &nbsp;
                                                                <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/word-by-word" target="_blank" rel="noopener noreferrer">
                                                                    Read more
                                                        </a>
                                                            </Typography>}>
                                                        <IconButton size="small">
                                                            <InfoIcon fontSize="small" className={classes.info}/>
                                                        </IconButton>
                                                    </Themes.HtmlTooltip>
                                                </Grid>
                                                <FormControl variant="outlined" disabled={!professionalSubscription}>
                                                    <FormLabel className={classes.label}>
                                                        Array Alignment
                                                    <Themes.HtmlTooltip
                                                            arrow
                                                            interactive
                                                            title={
                                                                <Typography variant="body2" color="inherit">This option gives you control over how items in arrays are matched for comparison. &nbsp;
                                                                    <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/array-alignment" target="_blank" rel="noopener noreferrer">
                                                                        Read more
                                                                    </a>
                                                                </Typography>}>
                                                            <IconButton size="small">
                                                                <InfoIcon fontSize="small" className={classes.info}/>
                                                            </IconButton>
                                                        </Themes.HtmlTooltip>
                                                    </FormLabel>
                                                    <RadioGroup className={classes.optionLabel} row aria-label="Array Alignment Priority" name={ArrayAlignmentName} value={configFormValues.ArrayAlignmentOption} onChange={handleChange}>
                                                        <FormControlLabel value={ArrayAlignment['orderless']} control={<Radio />} label="Orderless" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        <FormControlLabel value={ArrayAlignment['typeWithValuePriority']} control={<Radio />} label="Type With Value" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        <FormControlLabel value={ArrayAlignment['positionPriority']} control={<Radio />} label="Position" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid>)}
                                        {opContext.operationType === Operations.twoWayMerge && (
                                            <Grid item xs={12}>
                                                <FormControl variant="outlined">
                                                    <FormLabel className={classes.label}>
                                                        Merge Priority
                                                    <Themes.HtmlTooltip
                                                            arrow
                                                            interactive
                                                            title={
                                                                <Typography variant="body2" color="inherit">
                                                                    This option will give priority to the selected input on resolving modification &nbsp;
                                                                <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/two-way-merge-guide" target="_blank" rel="noopener noreferrer">
                                                                        Read more
                                                                </a>
                                                                </Typography>}>
                                                            <IconButton size="small">
                                                                <InfoIcon fontSize="small" className={classes.info}/>   
                                                            </IconButton>
                                                        </Themes.HtmlTooltip>
                                                    </FormLabel>
                                                    <RadioGroup className={classes.optionLabel} row aria-label="Array Alignment Priority" name={MergePriorityName} value={configFormValues.MergePriorityOption} onChange={handleChange}>
                                                        <FormControlLabel value={MergePriority['a']} control={<Radio />} label="A" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        <FormControlLabel value={MergePriority['b']} control={<Radio />} label="B" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid>)}
                                        {opContext.operationType === Operations.threeWayMerge && (
                                            <Grid item xs={12}>
                                                <FormControl variant="outlined" disabled={!professionalSubscription}>
                                                    <FormLabel className={classes.label}>
                                                        Conflicts Resolution
                                                    <Themes.HtmlTooltip
                                                            arrow
                                                            interactive
                                                            title={
                                                                <Typography variant="body2" color="inherit">
                                                                    This option gives control on resolving the conflicts between inputs. &nbsp;
                                                                <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/three-way-merge-guide" target="_blank" rel="noopener noreferrer">
                                                                        Read more
                                                                </a>
                                                                </Typography>}>
                                                            <IconButton size="small">
                                                                <InfoIcon fontSize="small" className={classes.info}/>
                                                            </IconButton>
                                                        </Themes.HtmlTooltip>
                                                    </FormLabel>
                                                    <RadioGroup className={classes.optionLabel} row aria-label="Conflicts" name={ThreeWayMergeModeName} value={configFormValues.ThreeWayMergeModeOption} onChange={handleChange}>
                                                        <FormControlLabel value={ThreeWayMergeMode['showConflicts']} control={<Radio />} label="Show" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        <FormControlLabel value={ThreeWayMergeMode['resolveUsingEdit1']} control={<Radio />} label="Resolve with input A" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        <FormControlLabel value={ThreeWayMergeMode['resolveUsingEdit2']} control={<Radio />} label="Resolve with input B" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Grid>)}
                                        {opContext.operationType === Operations.patch && (
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <FormControl variant="outlined">
                                                        <FormLabel className={classes.label}>
                                                            Patch Direction
                                                    <Themes.HtmlTooltip
                                                                arrow
                                                                interactive
                                                                title={
                                                                    <Typography variant="body2" color="inherit">
                                                                        This option decides the direction in which patch is applied. &nbsp;
                                                                <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/patch-guide" target="_blank" rel="noopener noreferrer">
                                                                            Read more
                                                                </a>
                                                                    </Typography>}>
                                                                <IconButton size="small">
                                                                    <InfoIcon fontSize="small" className={classes.info}/>
                                                                </IconButton>
                                                            </Themes.HtmlTooltip>
                                                        </FormLabel>
                                                        <RadioGroup className={classes.optionLabel} row aria-label="Array Alignment Priority" name={PatchDirectionName} value={configFormValues.PatchDirectionOption} onChange={handleChange}>
                                                            <FormControlLabel value={PatchDirection['aTob']} control={<Radio />} label="A to B" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                            <FormControlLabel value={PatchDirection['bToa']} control={<Radio />} label="B to A" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>)}
                                        {opContext.operationType === Operations.graft && (
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <FormControl variant="outlined">
                                                        <FormLabel className={classes.label}>
                                                            Graft Resolution Mode
                                                    <Themes.HtmlTooltip
                                                                arrow
                                                                interactive
                                                                title={
                                                                    <Typography variant="body2" color="inherit">
                                                                        This option controls how the resolution is handled. &nbsp;
                                                                <a className={classes.info} href="https://docs.deltaxml.com/deltajson/latest/graft-guide" target="_blank" rel="noopener noreferrer">
                                                                            Read more
                                                                </a>
                                                                    </Typography>}>
                                                                <IconButton size="small">
                                                                    <InfoIcon fontSize="small" className={classes.info}/>
                                                                </IconButton>
                                                            </Themes.HtmlTooltip>
                                                        </FormLabel>
                                                        <RadioGroup className={classes.optionLabel} row aria-label="Array Alignment Priority" name={GraftResolutionModeName} value={configFormValues.GraftResolutionModeOption} onChange={handleChange}>
                                                            <FormControlLabel value={GraftResolutionMode['changesetPriority']} control={<Radio />} label="Changeset Priority" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                            <FormControlLabel value={GraftResolutionMode['targetPriority']} control={<Radio />} label="Target Priority" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                            <FormControlLabel value={GraftResolutionMode['additionsOnly']} control={<Radio />} label="Additions Only" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>)}
                                    </Paper>
                                </Grid>
                            </Grid>
                            <Grid item xs={4}>
                                <Paper className={classes.comparePaper}>
                                    <List className={classes.root}>
                                        <Typography gutterBottom variant="h5" color="primary">
                                            Information
                                        </Typography>

                                        <React.Fragment>
                                            {opContext.operationType === Operations.compare && (
                                                <Typography gutterBottom className={classes.infoText} variant="body2" color="secondary">
                                                    Compares two JSON files and produces an output file in JSON format which describes the changes in a computer-readable form. Please see the <Link href="https://docs.deltaxml.com/deltajson/latest/compare-guide" target="_blank" className={classes.link}>Compare Guide</Link> for more details.
                                                </Typography>
                                            )}
                                            {opContext.operationType === Operations.twoWayMerge && (
                                                <Typography gutterBottom className={classes.infoText} variant="body2" color="secondary">
                                                    Two Way Merge is effectively a resolved comparison, where changes have been resolved to a specified version. Please see the <Link href="https://docs.deltaxml.com/deltajson/latest/two-way-merge-guide" target="_blank" className={classes.link}>Two Way Merge Guide</Link> for more details.
                                                </Typography>
                                            )}
                                            {opContext.operationType === Operations.threeWayMerge && (
                                                <Typography gutterBottom className={classes.infoText} variant="body2" color="secondary">
                                                    Three Way Merge operation merges a common ancestor file and two independent edits of the ancestor to produce either a resolved result or a result showing conflicts. Please see the <Link href="https://docs.deltaxml.com/deltajson/latest/three-way-merge-guide" target="_blank" className={classes.link}>Three Way Merge Guide</Link> for more details.
                                                </Typography>
                                            )}
                                            {opContext.operationType === Operations.graft && (
                                                <Typography gutterBottom className={classes.infoText} variant="body2" color="secondary">
                                                    Graft applies changes defined in a JSON Delta file (changeset) to any other similar JSON data file. Please see the <Link href="https://docs.deltaxml.com/deltajson/latest/graft-guide" target="_blank" className={classes.link}>Graft Guide</Link> for more details.
                                                </Typography>
                                            )}
                                            {opContext.operationType === Operations.patch && (
                                                <Typography gutterBottom className={classes.infoText} variant="body2" color="secondary">
                                                    Generates a standard <Link href="https://tools.ietf.org/html/rfc6902" target="_blank" className={classes.link} color="secondary">JSON Patch</Link> which can be applied to a JSON file. Please see the <Link href="https://docs.deltaxml.com/deltajson/latest/patch-guide" target="_blank" className={classes.link}>Patch Guide</Link> for more details.
                                                </Typography>
                                            )}

                                            <Typography gutterBottom variant="body2" color="primary">
                                                Further Resources
                                            </Typography>

                                            {opContext.operationType !== Operations.graft &&
                                                opContext.operationType !== Operations.patch && (
                                                    <ListItem alignItems="flex-start" className={classes.listItem}>
                                                        <ListItemText
                                                            primary={<Link href="https://docs.deltaxml.com/deltajson/latest/word-by-word" target="_blank" className={classes.link} color="secondary">Word by Word</Link>}
                                                        />
                                                    </ListItem>
                                                )}
                                            {opContext.operationType !== Operations.graft &&
                                                opContext.operationType !== Operations.patch && (
                                                    <ListItem alignItems="flex-start" className={classes.listItem}>
                                                        <ListItemText
                                                            primary={<Link href="https://docs.deltaxml.com/deltajson/latest/array-alignment" target="_blank" className={classes.link} color="secondary">Array Alignment</Link>}
                                                        />
                                                    </ListItem>
                                                )}
                                            {opContext.operationType === Operations.twoWayMerge && (
                                                <ListItem alignItems="flex-start" className={classes.listItem}>
                                                    <ListItemText
                                                        primary={<Link href="https://docs.deltaxml.com/deltajson/latest/two-way-merge-guide" target="_blank" className={classes.link} color="secondary">Merge Priority</Link>}
                                                    />
                                                </ListItem>
                                            )}
                                            {opContext.operationType === Operations.threeWayMerge && (
                                                <ListItem alignItems="flex-start" className={classes.listItem}>
                                                    <ListItemText
                                                        primary={<Link href="https://docs.deltaxml.com/deltajson/latest/three-way-merge-guide" target="_blank" className={classes.link} color="secondary">Conflicts Resolution</Link>}
                                                    />
                                                </ListItem>
                                            )}
                                            {opContext.operationType === Operations.graft && (
                                                <ListItem alignItems="flex-start" className={classes.listItem}>
                                                    <ListItemText
                                                        primary={<Link href="https://docs.deltaxml.com/deltajson/latest/graft-guide#id-(latest)GraftGuide-paramsGraftParameters" target="_blank" className={classes.link} color="secondary">Graft Resolution Mode</Link>}
                                                    />
                                                </ListItem>
                                            )}
                                            {opContext.operationType === Operations.patch && (
                                                <ListItem alignItems="flex-start" className={classes.listItem}>
                                                    <ListItemText
                                                        primary={<Link href="https://docs.deltaxml.com/deltajson/latest/patch-guide" target="_blank" className={classes.link} color="secondary">Patch Direction</Link>}
                                                    />
                                                </ListItem>
                                            )}
                                        </React.Fragment>
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </div>
                </Paper>
            )}
        </OperationContext.Consumer>
    );

}


export default withStyles(styles)(ConfigContent);